import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ApiService } from './api.service';

interface AiJob {
  id: string;
  status: 'processing' | 'completed' | 'error';
  doc_status?: string;
  reference_id?: string;
  model_type?: 'Job' | 'Project' | 'Manual';
  title?: string;
  source_image_url?: string;
  message?: string;
  created_at: string;
  reviewed_at?: string;
  generated_at?: string;
}

type AttachTab = 'Project' | 'Job';
type ExtractionMode = 'full' | 'boundary_only';

@Component({
  selector: 'app-admin-as-built-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-as-built-dashboard.component.html',
  styleUrls: ['./admin-as-built-dashboard.component.css'],
})
export class AdminAsBuiltDashboardComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private router = inject(Router);
  private api = inject(ApiService);

  jobs = signal<AiJob[]>([]);
  isLoadingJobs = signal(true);
  isUploading = signal(false); // just briefly while the multipart goes up, not the full 100sec AI process!
  title = signal('');
  extractionMode = signal<ExtractionMode>('full');
  defaultDrawingWidthMetres = signal(30);
  attachModalJob = signal<AiJob | null>(null);
  attachTab = signal<AttachTab>('Project');
  projects = signal<any[]>([]);
  jobsList = signal<any[]>([]);
  isLoadingAttachList = signal(false);

  private pollInterval: any;

  ngOnInit(): void {
    this.loadDefaultScale();
    this.fetchJobs();
    this.pollInterval = setInterval(() => this.fetchJobs(), 10000);
  }

  ngOnDestroy(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
  }

  private fetchJobs(): void {
    this.http.get<any>(`${this.api.baseUrl}/ai/list_drainage_jobs.php`, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          this.isLoadingJobs.set(false);
          if (res?.success) {
            this.jobs.set(res.data.jobs || []);
          }
        },
        error: (e) => { this.isLoadingJobs.set(false); console.error('Failed to load jobs', e); }
      });
  }

  onFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (!file) return;

    this.isUploading.set(true);

    const formData = new FormData();
    formData.append('plan_image', file);
    formData.append('title', this.title() || file.name.replace(/\.[^.]+$/, ''));
    formData.append('extraction_mode', this.extractionMode());

    this.http.post<any>(`${this.api.baseUrl}/ai/extract_drainage.php`, formData, { headers: (this.api as any).getHeaders().delete('Content-Type') })
      .subscribe({
        next: (res) => {
          this.isUploading.set(false);
          if (res?.success) {
            this.title.set('');
            this.fetchJobs(); // immediately show the new "processing" job on the grid
          } else {
            alert(res?.message || 'Upload failed');
          }
        },
        error: (e) => {
          this.isUploading.set(false);
          alert('Upload failed: ' + (e.error?.message || e.message));
        }
      });
  }

  openJob(job: AiJob): void {
    if (job.status === 'completed') {
      this.router.navigate(['/admin/ai/as-built', job.id]);
    } else {
      alert('This job is ' + job.status + ' and cannot be opened yet.');
    }
  }

  jobStatusClass(job: AiJob): string {
    const docStatus = String(job.doc_status || '').trim().toLowerCase();
    if (job.status === 'completed' && (!docStatus || docStatus === 'needs review')) {
      return 'status-completed-review';
    }
    return 'status-' + job.status;
  }

  deleteJob(id: string, event: Event): void {
    event.stopPropagation();
    if (!confirm('Are you sure you want to permanently delete this extraction job?')) return;

    this.http.post<any>(`${this.api.baseUrl}/ai/delete_drainage_job.php`, { job_id: id }, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          if (res?.success) this.fetchJobs();
          else alert(res?.message || 'Delete failed');
        },
        error: (e) => alert('Delete failed: ' + (e.error?.message || e.message))
      });
  }

  createManualPlan(): void {
    this.http.post<any>(`${this.api.baseUrl}/ai/create_manual_drainage_job.php`, {
      title: this.title() || 'Manual as-built plan',
      drawing_width_metres: this.defaultDrawingWidthMetres()
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success && res.data?.job_id) {
          this.router.navigate(['/admin/ai/as-built', res.data.job_id]);
        } else {
          alert(res?.message || 'Could not create manual plan');
        }
      },
      error: (e) => alert('Could not create manual plan: ' + (e.error?.message || e.message))
    });
  }

  openTraining(): void {
    this.router.navigate(['/admin/ai/as-built/training']);
  }

  openAttachModal(job: AiJob, event: Event): void {
    event.stopPropagation();
    if (job.status !== 'completed') {
      alert('Attach is available once processing is complete.');
      return;
    }
    this.attachModalJob.set(job);
    this.attachTab.set('Project');
    this.loadAttachList('Project');
  }

  closeAttachModal(): void {
    this.attachModalJob.set(null);
  }

  setAttachTab(tab: AttachTab): void {
    this.attachTab.set(tab);
    this.loadAttachList(tab);
  }

  private loadAttachList(tab: AttachTab): void {
    this.isLoadingAttachList.set(true);
    const request = tab === 'Project' ? this.api.getProjects() : this.api.getJobs();
    request.subscribe({
      next: (res: any) => {
        this.isLoadingAttachList.set(false);
        if (tab === 'Project') this.projects.set(res?.data?.projects || []);
        else this.jobsList.set(res?.data?.jobs || []);
      },
      error: () => {
        this.isLoadingAttachList.set(false);
        alert(`Could not load ${tab.toLowerCase()} list`);
      }
    });
  }

  attachToReference(reference: any): void {
    const job = this.attachModalJob();
    if (!job) return;
    this.http.post<any>(`${this.api.baseUrl}/ai/attach_drainage_job.php`, {
      job_id: job.id,
      model_type: this.attachTab(),
      reference_id: reference.id
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success) {
          this.closeAttachModal();
          this.fetchJobs();
        } else {
          alert(res?.message || 'Attach failed');
        }
      },
      error: (e) => alert('Attach failed: ' + (e.error?.message || e.message))
    });
  }

  attachList(): any[] {
    return this.attachTab() === 'Project' ? this.projects() : this.jobsList();
  }

  referenceTitle(item: any): string {
    return item.title || item.name || item.job_title || item.customer_name || item.address || item.id;
  }

  private loadDefaultScale(): void {
    const local = Number(localStorage.getItem('asBuiltDefaultDrawingWidthMetres'));
    if (Number.isFinite(local) && local > 0) {
      this.defaultDrawingWidthMetres.set(local);
    }
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const dbDefault = Number(res?.data?.as_built_default_drawing_width_metres);
        if (Number.isFinite(dbDefault) && dbDefault > 0) {
          this.defaultDrawingWidthMetres.set(dbDefault);
          localStorage.setItem('asBuiltDefaultDrawingWidthMetres', String(dbDefault));
        }
      }
    });
  }

  sourceImageHref(job: AiJob): string | null {
    if (!job.source_image_url) return null;
    return this.api.assetUrl(job.source_image_url);
  }

  jobTitle(job: AiJob): string {
    return job.title || `As-built ${job.id.split('-').pop()?.substring(0, 6) || job.id}`;
  }

  formatDate(d: string): string {
    try {
      const dt = new Date(d);
      return dt.toLocaleString('en-NZ', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
        timeZone: 'Pacific/Auckland', timeZoneName: 'short'
      });
    } catch { return d; }
  }
}
